<template>
  <!-- eslint-disable max-len -->
  <div
    class="rating_block"
    :class="{
      'rating_block--chosen': !isCosmeticRating,
      'rating_block--empty': value === 0,
      'rating_block--half': value === 0.5,
      'rating-block--full': value === 1,
    }"
  >
    <div class="rating_block__hover-animation">
      <div class="rating_block__body">
        <svg
          v-if="type === 'stars'"
          class="rating_block__star"
          :class="{
            'rating_block__star--red': color === 'red',
            'rating_block__star--orange': color === 'orange',
            'rating_block__star--green': color === 'green',
            'rating_block__star--grey': value === 0,
            'rating_block__star--half': value === 0.5,
          }"
          width="32px"
          height="32px"
          viewBox="0 0 32 32"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <path
            v-if="value === 0"
            d="M25.2217554,30.7026384 L21.7381229,19.6118243 L30.5556748,12.6756098 L19.5404455,12.6756098 L16,1.63713248 L12.4595545,12.6756098 L1.44432523,12.6756098 L10.2618771,19.6118243 L6.77824465,30.7026384 L16,23.9839069 L25.2217554,30.7026384 Z"
            id="Star-Shape"
          ></path>
          <g v-if="value === 0.5">
            <polygon
              id="Star-Shape"
              points="26.1533423 32 22.3185998 19.7913596 32 12.1756098 19.9051657 12.1756098 16 0 12.0948343 12.1756098 1.38777878e-17 12.1756098 9.68140023 19.7913596 5.84665771 32 16 24.6025385"
            ></polygon>
            <path
              d="M25.2217554,30.7026384 L21.7381229,19.6118243 L30.5556748,12.6756098 L19.5404455,12.6756098 L16.5,3.19604325 L16.5,24.3481939 L25.2217554,30.7026384 Z"
              id="Star-Shape-half"
              stroke-width="1"
              fill="#FFFFFF"
            ></path>
          </g>
          <path
            v-if="value === 1"
            class="star star--full"
            stroke-width="1"
            d=" M25.2217554,30.7026384 L21.7381229,19.6118243 L30.5556748,12.6756098
                            L19.5404455,12.6756098 L16,1.63713248 L12.4595545,12.6756098 L1.44432523,12.6756098
                            L10.2618771,19.6118243 L6.77824465,30.7026384 L16,23.9839069 L25.2217554,30.7026384 Z"
            id="Star-Shape"
          ></path>
        </svg>
        <slot v-if="type !== 'stars'"></slot>
      </div>
      <div
        @mouseup="select('half')"
        @mouseover="onHover('half')"
        class="rating_block__background-half rating_block__background-half--first"
        :class="{
          'rating_block__background-half--red': color === 'red',
          'rating_block__background-half--orange': color === 'orange',
          'rating_block__background-half--green': color === 'green',
          'rating_block__background-half--grey': value === 0,
        }"
        ref="halfFirst"
        :data-rating="setRating"
      ></div>
      <div
        @mouseup="select('full')"
        @mouseover="onHover('full')"
        class="rating_block__background-half rating_block__background-half--last"
        :class="{
          'rating_block__background-half--red': color === 'red',
          'rating_block__background-half--orange': color === 'orange',
          'rating_block__background-half--green': color === 'green',
          'rating_block__background-half--grey': value <= 0.5,
        }"
        ref="halfLast"
        :data-rating="setRating"
      ></div>
    </div>
  </div>
</template>

<script>
import isMobile from "is-mobile";
import BaseComponent from "../BaseComponent";

/**
 * @group Inputs - parts
 * This is just a singular block with a star or a smiley in it.
 * This block is used in the rating input.
 */
export default {
  name: "RatingBlock",
  mixins: [BaseComponent],
  props: {
    // Defines if the block is empty (0), half full (0.5) or full (1)
    value: {
      // `0`/ `0.5` / `1`
      type: Number,
      required: true,
    },
    // Defines the color of the block
    color: {
      // `'red'` / `'orange'` / `'green'`
      type: String,
      default: "green",
    },
    // The rating the block is going to emit when selected
    setRating: {
      type: Number,
      required: true,
    },
    // Used for hover states, allows to not set rating, whilst still showing that rating
    isCosmeticRating: {
      type: Boolean,
      required: false,
    },
    // When disabled, you can only select the whole block
    allowHalves: {
      type: Boolean,
      default: false,
    },
    // This block can either show smileys or stars
    type: {
      // `'stars'`/ `'smileys`
      type: String,
      default: "stars",
      validator(value) {
        return ["stars", "smileys"].indexOf(value) !== -1;
      },
    },
  },
  computed: {
    isMobile() {
      return isMobile();
    },
  },
  methods: {
    select(type) {
      let { setRating } = this;
      if (type === "half" && this.allowHalves) setRating -= 1;
      this.$emit("onSelect", setRating);
    },
    onHover(type) {
      if (this.isMobile) return;
      let { setRating } = this;
      if (type === "half" && this.allowHalves) setRating -= 1;
      this.$emit("onHover", setRating);
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";
$empty_border_color: $grey_french;

.rating_block {
  width: 20%;
  max-width: 72px;
  height: 72px;
  position: relative;

  &:hover {
    cursor: pointer;
    .rating_block__hover-animation {
      transform: translateY(-10px);
    }
  }
  &.rating_block--chosen {
    .rating_block__hover-animation {
      transition: 90ms all ease-out;
      transform: translateY(0px);
    }
  }
}
.rating_block__hover-animation {
  width: 100%;
  height: 100%;
  transition: 100ms all;
}
.rating_block__body {
  width: 100%;
  height: 100%;
  font-size: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
  pointer-events: none;
}
.rating_block__star {
  &.rating_block__star--red {
    stroke: $red;
    fill: $red;
  }
  &.rating_block__star--orange {
    stroke: $orange;
    fill: $orange;
  }
  &.rating_block__star--green {
    stroke: $green;
    fill: $green;
  }
  &.rating_block__star--grey {
    stroke: $empty_border_color;
    fill: rgba(0, 0, 0, 0);
  }
}
.rating_block__background-half {
  width: 50%;
  height: 100%;
  top: 0px;
  position: absolute;
  border: 1px solid $empty_border_color;
  background-color: $white;
  border-style: solid;
  &.rating_block__background-half--first {
    left: 0px;
    border-right-width: 0px;
    border-radius: 8px 0px 0px 8px;
    border-left-width: 1px;
  }
  &.rating_block__background-half--last {
    right: 0px;
    border-left-width: 0px;
    border-radius: 0px 8px 8px 0px;
  }
  &.rating_block__background-half--red {
    background-color: rgba($red, 0.05);
    border-color: $red;
  }
  &.rating_block__background-half--orange {
    background-color: rgba($orange, 0.05);
    border-color: $orange;
  }
  &.rating_block__background-half--green {
    background-color: rgba($green, 0.05);
    border-color: $green;
  }
  &.rating_block__background-half--grey {
    background-color: $white;
    border-color: $empty_border_color;
  }
}
</style>
